import { useEffect, useMemo, useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { MyEventRow, eventsByDay, useMyEvents } from "@/components/events/MyEvents";

type SchoolEvent = {
  id: string;
  date: string;
  kind: string;
  title: string;
  note: string | null;
};

const KIND_LABEL: Record<string, string> = {
  no_school: "No school",
  holiday: "Holiday",
  early_release: "Early release",
  testing: "Testing",
  other: "School day",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function dayKey(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function monthGrid(month: Date) {
  const first = startOfMonth(month);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  start.setHours(0, 0, 0, 0);
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - last.getDay()));
  end.setHours(0, 0, 0, 0);
  const days: Date[] = [];
  for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    days.push(new Date(cursor));
  }
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  const rangeEnd = new Date(end);
  rangeEnd.setDate(rangeEnd.getDate() + 1);
  return { weeks, start, end: rangeEnd };
}

export default function ParentCalendar() {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState<string | null>(null);
  const [events, setEvents] = useState<SchoolEvent[] | null>(null);
  const [error, setError] = useState("");
  const grid = useMemo(() => monthGrid(month), [month]);
  const todayKey = dayKey(new Date());
  const lastDay = useMemo(() => new Date(grid.end.getTime() - 1), [grid.end]);
  const mine = useMyEvents(dayKey(grid.start), dayKey(lastDay));
  const mineByDay = useMemo(() => eventsByDay(mine.events), [mine.events]);

  useEffect(() => {
    let cancel = false;
    setError("");
    setEvents(null);
    const from = grid.start.toISOString();
    const to = grid.end.toISOString();
    api<{ success: boolean; data: { events: SchoolEvent[] } }>(
      `/api/parent/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    )
      .then((env) => {
        if (!cancel) setEvents(env.data?.events || []);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [grid.start, grid.end]);

  const byDay = useMemo(() => {
    const map = new Map<string, SchoolEvent[]>();
    for (const event of events || []) {
      const list = map.get(event.date) || [];
      list.push(event);
      map.set(event.date, list);
    }
    return map;
  }, [events]);

  useEffect(() => {
    if (!events || selected) return;
    const now = new Date();
    if (month.getMonth() === now.getMonth() && month.getFullYear() === now.getFullYear()) {
      setSelected(todayKey);
    }
  }, [events, month, selected, todayKey]);

  const selectedEvents = selected ? byDay.get(selected) || [] : [];
  const selectedMine = selected ? mineByDay.get(selected) || [] : [];
  const monthLabel = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const selectedLabel = selected
    ? new Date(`${selected}T12:00:00`).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";

  if (error) {
    return (
      <div className="student-page student-page--cal">
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="student-page student-page--wide student-page--cal">
      <div className="student-page-head">
        <span className="student-eyebrow">
          <CalendarIcon size={12} aria-hidden /> Calendar
        </span>
        <div className="student-cal-head">
          <h1>{monthLabel}</h1>
          <div className="student-cal-toolbar">
            <button
              type="button"
              className="student-text-link"
              onClick={() => {
                setMonth(startOfMonth(new Date()));
                setSelected(todayKey);
              }}
            >
              Today
            </button>
            <button
              type="button"
              className="student-cal-nav"
              aria-label="Previous month"
              onClick={() => {
                setSelected(null);
                setMonth((current) => addMonths(current, -1));
              }}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              className="student-cal-nav"
              aria-label="Next month"
              onClick={() => {
                setSelected(null);
                setMonth((current) => addMonths(current, 1));
              }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {!events ? (
        <div className="student-cal" aria-busy="true" aria-live="polite">
          <span className="student-vh">Loading the school calendar</span>
          <div className="student-cal-grid student-cal-grid--ghost" role="presentation">
            {WEEKDAYS.map((day) => (
              <div key={day} className="student-cal-dow">
                {day}
              </div>
            ))}
            {grid.weeks.flat().map((day, i) => {
              const inMonth = day.getMonth() === month.getMonth();
              return (
                <div key={dayKey(day)} className={`student-cal-cell${inMonth ? "" : " is-outside"}`}>
                  <span className="student-cal-cell__n">{day.getDate()}</span>
                  <span className="student-cal-cell__items">
                    {inMonth && i % 4 === 1 ? <span className="student-ghost student-ghost--cal-pill" /> : null}
                    {inMonth && i % 6 === 2 ? (
                      <span className="student-ghost student-ghost--cal-pill student-ghost--w70" />
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="student-cal">
          <div className="student-cal-grid" role="grid" aria-label={monthLabel}>
            {WEEKDAYS.map((day) => (
              <div key={day} className="student-cal-dow" role="columnheader">
                {day}
              </div>
            ))}
            {grid.weeks.flat().map((day) => {
              const key = dayKey(day);
              const inMonth = day.getMonth() === month.getMonth();
              const items = byDay.get(key) || [];
              const own = mineByDay.get(key) || [];
              const ownShown = own.slice(0, Math.max(0, 3 - items.length));
              const total = items.length + own.length;
              return (
                <button
                  key={key}
                  type="button"
                  role="gridcell"
                  className={`student-cal-cell${inMonth ? "" : " is-outside"}${key === todayKey ? " is-today" : ""}${key === selected ? " is-selected" : ""}`}
                  onClick={() => setSelected(key)}
                  aria-label={`${day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}${total ? `, ${[...items.map((item) => item.title), ...own.map((event) => event.title)].join(", ")}` : ""}`}
                >
                  <span className="student-cal-cell__n">{day.getDate()}</span>
                  <span className="student-cal-cell__items">
                    {items.slice(0, 3).map((item) => (
                      <span
                        key={item.id}
                        className={`student-cal-pill student-cal-pill--school${item.kind === "no_school" || item.kind === "holiday" ? " is-off" : ""}`}
                      >
                        <span className="student-cal-pill__t">{item.title}</span>
                      </span>
                    ))}
                    {ownShown.map((event) => (
                      <span key={event.id} className="student-cal-pill student-cal-pill--event">
                        <span className="student-cal-pill__t">{event.title}</span>
                      </span>
                    ))}
                    {total > 3 ? <span className="student-cal-more">+{total - 3} more</span> : null}
                  </span>
                </button>
              );
            })}
          </div>

          {selected ? (
            <div className="student-card student-cal-day" style={{ marginTop: 14 }}>
              <span className="student-eyebrow">{selectedLabel}</span>
              {selectedMine.length ? (
                <div className="student-cal-events">
                  {selectedMine.map((event) => (
                    <MyEventRow key={event.id} event={event} canRespond={mine.canRespond} onChange={mine.replace} />
                  ))}
                </div>
              ) : null}
              {selectedEvents.length === 0 ? (
                selectedMine.length ? null : (
                  <p className="student-row__sub" style={{ marginTop: 8 }}>
                    A regular school day.
                  </p>
                )
              ) : (
                selectedEvents.map((item) => (
                  <div key={item.id} className="student-row">
                    <span>
                      <strong>{item.title}</strong>
                      <span className="student-row__sub">
                        {KIND_LABEL[item.kind] || "School day"}
                        {item.note ? ` · ${item.note}` : ""}
                      </span>
                    </span>
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
